import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeamMembersHash, selectAuthToken } from 'selectors';
import { resetMemberPassword } from 'thunk';
import * as ProfileServices from 'ProfileModule/services';
import ApiClientManager from 'core/apiClient';
import { useNotification } from 'hooks/useNotification';

export interface UseResetMemberPasswordModalOptions {
  /** Tenant name for API context - if not provided, falls back to global tenant */
  tenant?: string;
  /** Realm ID for API context - if not provided, falls back to global realm */
  realmId?: string;
}

export const useResetMemberPasswordModal = (options?: UseResetMemberPasswordModalOptions) => {
  const { openModal } = useDynamicModal();
  const dispatch = useDispatch();
  const membersHash = useSelector(selectTeamMembersHash);
  const token = useSelector(selectAuthToken);
  const { tenant, realmId } = options || {};
  const { showSuccess, showError } = useNotification();

  const openResetMembersPasswordModal = useCallback(
    (args: { accountIds: number[]; onConfirm?: VoidFunction }) => {
      const onConfirmRequestPassword = async () => {
        const emails = args.accountIds.map((id) => membersHash[id]?.email);

        try {
          // Use explicit tenant/realm context if provided
          if (tenant && realmId && token) {
            const headers = ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId });
            await ProfileServices.resetMemberPassword({ emails }, { headers }, token);
            showSuccess('Password reset email sent successfully');
          } else {
            // Fall back to global context via thunk
            // TODO: Fix with Redux Toolkit configureStore migration
            dispatch(resetMemberPassword({ emails }) as any);
          }
          args.onConfirm?.();
        } catch (e: any) {
          const apiMessage = e?.data?.message || e?.response?.data?.message || e?.message;
          const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '. Please try again.';
          showError(`Failed to send password reset email${apiMessageSuffix}`);
        }
      };

      openModal({
        modal: ({ toggle }) => {
          return (
            <SimpleModal
              onClose={toggle}
              open
              onConfirm={onConfirmRequestPassword}
              closeOnConfirm
              Body={
                <>
                  {' '}
                  <div>
                    You are about to send a reset password email to the following:
                    {args.accountIds.map((id) => (
                      <li key={id} style={{ margin: '1rem 0' }}>
                        {membersHash[id]?.name}
                      </li>
                    ))}
                  </div>
                </>
              }
            />
          );
        },
      });
    },
    [dispatch, membersHash, openModal, tenant, realmId, token, showSuccess, showError],
  );
  return {
    openResetMembersPasswordModal,
  };
};
